import { useEffect, useState } from "react";
import { Check, X, Globe } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loading, EmptyState } from "@/components/States";
import { REVIEW_STYLE } from "@/components/admin/AdminJobs";
import { HiringStats } from "@/components/hiring/HiringStats";
import { api, formatError } from "@/lib/api";

function EmployerRow({ e, onDone }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const decide = async (status) => {
    setBusy(true);
    try { await api.patch(`/admin/employers/${e.id}`, { status, note }); toast.success(`${e.company?.name} ${status}; employer is being notified`); onDone(); } catch (err) { toast.error(formatError(err)); }
    setBusy(false);
  };
  const st = e.employer_status;
  return (
    <div data-testid={`employer-${e.id}`} className="rounded-xl border border-line bg-surface p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="font-semibold text-ink">{e.company?.name}
            <span data-testid={`employer-status-${e.id}`} className={`ml-2 rounded-full px-2 py-0.5 text-[11px] font-medium capitalize ${REVIEW_STYLE[st === "approved" ? "approved" : st === "rejected" ? "rejected" : "pending"]}`}>{st}</span></p>
          <p className="text-sm text-ink-2">{e.name} · {e.email}</p>
          <p className="mt-0.5 flex flex-wrap items-center gap-3 text-xs text-ink-3">
            {e.company?.website && <a href={e.company.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-aqua hover:underline"><Globe className="h-3 w-3" />{e.company.website}</a>}
            <span>Registered {new Date(e.created_at).toLocaleDateString()}</span>
            {e.review_note && <span>Note: {e.review_note}</span>}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Input data-testid={`employer-note-${e.id}`} value={note} onChange={(ev) => setNote(ev.target.value)} maxLength={500} placeholder="Optional note to employer" className="h-8 w-56" />
          {st !== "approved" && <Button size="sm" disabled={busy} data-testid={`employer-approve-${e.id}`} onClick={() => decide("approved")} className="rounded-full bg-emerald-700 hover:bg-emerald-800"><Check className="mr-1 h-3.5 w-3.5" />Approve</Button>}
          {st !== "rejected" && <Button size="sm" variant="outline" disabled={busy} data-testid={`employer-reject-${e.id}`} onClick={() => decide("rejected")} className="rounded-full border-red-200 text-red-700"><X className="mr-1 h-3.5 w-3.5" />{st === "approved" ? "Revoke" : "Reject"}</Button>}
        </div>
      </div>
    </div>
  );
}

export default function AdminEmployers() {
  const [items, setItems] = useState(null);
  const load = () => api.get("/admin/employers").then((r) => setItems(r.data)).catch((e) => toast.error(formatError(e)));
  useEffect(() => { load(); }, []);
  if (!items) return <Loading />;
  const pending = items.filter((e) => e.employer_status === "pending").length;
  return (
    <div className="flex flex-col gap-4">
      <HiringStats />
      <p className="text-sm text-ink-3"><b data-testid="employers-pending-count" className="text-ink">{pending}</b> awaiting approval. Approved employers can submit jobs for review and manage their own applicants (learner emails stay hidden). Rejecting or revoking takes their jobs offline.</p>
      {items.length === 0 ? <EmptyState testId="employers-empty" title="No employer accounts yet" text="Companies that register at /employer/register appear here." /> : (
        <div data-testid="employers-list" className="flex flex-col gap-3">{items.map((e) => <EmployerRow key={e.id} e={e} onDone={load} />)}</div>
      )}
    </div>
  );
}
